import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { ImpressaoPedido, SituacaoPedidoCaixa, ValePedido } from '../types';

interface Props {
  aberto: boolean;
  pedidoId: number | null;
  aoFechar: () => void;
  aoFim: (mensagem: string, deuCerto: boolean) => void;
}

const ROTULO_STATUS: Record<string, string> = {
  impressa: 'impressa',
  enviada: 'enviada',
  erro: 'deu erro',
};

// Folha "Quais fichas faltaram?": marca as que precisam sair de novo.
export default function FolhaReimprimir({ aberto, pedidoId, aoFechar, aoFim }: Props) {
  const [vales, setVales] = useState<ValePedido[]>([]);
  const [impressoes, setImpressoes] = useState<ImpressaoPedido[]>([]);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto || pedidoId == null) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('caixa_situacao_pedido', { p_pedido: pedidoId });
        if (error) throw error;
        if (!ativo) return;
        const s = (data as SituacaoPedidoCaixa) ?? null;
        const lista = (s?.vales ?? []) as ValePedido[];
        setVales(lista);
        setImpressoes((s?.impressoes ?? []) as ImpressaoPedido[]);
        setMarcadas(new Set(lista.map((v) => v.id)));
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [aberto, pedidoId]);

  function alternar(id: number) {
    setMarcadas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function statusDe(valeId: number): string | null {
    const imp = impressoes.find((i) => i.vale_id === valeId);
    if (!imp) return null;
    return ROTULO_STATUS[imp.status] ?? imp.status;
  }

  async function reimprimir() {
    if (pedidoId == null) return;
    setEnviando(true);
    setErro(null);
    try {
      const valeIds = vales.filter((v) => marcadas.has(v.id)).map((v) => v.id);
      const { data, error } = await supabase.functions.invoke('caixa-imprimir', {
        body:
          valeIds.length === vales.length
            ? { pedido_id: pedidoId, reimpressao: true }
            : { pedido_id: pedidoId, reimpressao: true, vale_ids: valeIds },
      });
      if (error) {
        setErro('Sem conexão agora. Tente de novo.');
        return;
      }
      const deuCerto =
        data?.ok === true &&
        Number(data?.total) > 0 &&
        Number(data?.impressas) === Number(data?.total);
      aoFim(
        data?.mensagem || data?.erro || (deuCerto ? 'Reimpressão enviada.' : 'Não deu para reimprimir.'),
        deuCerto,
      );
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  const nadaMarcado = marcadas.size === 0;

  return (
    <Modal aberto={aberto} titulo="Quais fichas faltaram?" aoFechar={aoFechar}>
      <p className="text-sm text-secondary-500">
        Troque o rolo antes. Ficha repetida não faz mal: a barraca aceita cada QR uma vez.
      </p>

      {carregando ? (
        <div className="mt-4 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary-100" />
          ))}
        </div>
      ) : erro ? (
        <p className="mt-4 text-sm text-danger-600">{erro}</p>
      ) : vales.length === 0 ? (
        <p className="mt-4 text-sm text-secondary-500">
          Não consegui listar as fichas. Você pode reimprimir todas de uma vez.
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={() =>
                setMarcadas(
                  marcadas.size === vales.length ? new Set() : new Set(vales.map((v) => v.id)),
                )
              }
              className="cursor-pointer whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
            >
              {marcadas.size === vales.length ? 'Desmarcar todas' : 'Todas'}
            </button>
          </div>
          <ul className="mt-2 space-y-2">
            {vales.map((v) => {
              const marcada = marcadas.has(v.id);
              const status = statusDe(v.id);
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => alternar(v.id)}
                    className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                      marcada ? 'border-primary-500 bg-primary-50' : 'border-secondary-200 bg-white'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${
                        marcada
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-secondary-300 bg-white'
                      }`}
                    >
                      {marcada && <i className="ri-check-line text-sm" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-foreground-950">
                        Nº {v.numero}
                      </span>
                      {(v.produto || v.nome || v.ponto) && (
                        <span className="block truncate text-xs text-secondary-500">
                          {[v.produto || v.nome, v.ponto].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    {status && (
                      <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700">
                        {status}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao
          variante="primario"
          icone="ri-printer-line"
          carregando={enviando}
          disabled={nadaMarcado && vales.length > 0}
          onClick={reimprimir}
        >
          {vales.length === 0 || marcadas.size === vales.length
            ? 'Reimprimir todas'
            : 'Reimprimir marcadas'}
        </Botao>
      </div>
    </Modal>
  );
}